import React, { useState, useEffect, useRef } from 'react';

/**
 * Hybrid search bar with keyword, semantic, and natural language support
 * Supports toggling between Fast Keyword Search and AI Semantic Search
 * Searches across multiple fields: site, address, submitter, city, branch, provider, etc.
 */
const ApprovalSearchBar = ({ value, onChange, placeholder, onSearchModeChange, onOpenChat }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [isSearching, setIsSearching] = useState(false);
  const [showHints, setShowHints] = useState(false);
  // Default to keyword since we are hiding the separate AI Search toggle
  const [searchMode, setSearchMode] = useState('keyword');
  const debounceTimerRef = useRef(null);

  // Notify parent when search mode changes (kept for compatibility in case we restore logic)
  const toggleSearchMode = () => {
    // Legacy toggle logic replaced by Chat button as per user request
  };

  // Update local value when prop changes (e.g., from URL)
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  // Update local value only (don't trigger search until Enter)
  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Optional: Keep auto-search but with longer delay (1.5 seconds)
    setIsSearching(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      onChange(newValue, searchMode);
      setIsSearching(false);
    }, 1500);
  };

  // Clear search
  const handleClear = () => {
    setLocalValue('');
    onChange('', searchMode);
    setIsSearching(false);
  };

  // Detect search type based on query
  const getSearchType = () => {
    if (!localValue) return null;
    if (localValue.includes('?') || localValue.split(' ').length > 3) {
      return { type: 'Natural Language', icon: '✨', color: 'text-[var(--pbfs-green)]' };
    } else if (/^[0-9]+$/.test(localValue)) {
      return { type: 'Zip Code', icon: '📍', color: 'text-[var(--pbfs-blue)]' };
    } else if (localValue.includes('@')) {
      return { type: 'Email Search', icon: '📧', color: 'text-[var(--pbfs-green)]' };
    }
    return { type: 'Keyword', icon: '🔍', color: 'text-[var(--pbfs-green)]' };
  };

  const searchType = getSearchType();

  return (
    <div className="relative">
      {/* Search Mode Toggle - Compact on Mobile */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mb-3">
        <div className="flex items-center gap-1 bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-lg p-0.5 sm:p-1">
          <button
            onClick={() => {
              // Always enforce keyword mode if they click Fast Search
              setSearchMode('keyword');
              localStorage.setItem('adminSearchMode', 'keyword');
              if (onSearchModeChange) onSearchModeChange('keyword');
            }}
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${searchMode === 'keyword'
                ? 'bg-[var(--pbfs-blue)] text-white shadow-lg'
                : 'text-white/60 hover:text-white hover:bg-white/10'
              }`}
          >
            <span className="hidden sm:inline">⚡ Fast Search</span>
            <span className="sm:hidden">⚡ Fast</span>
          </button>

          {/* Ask Pye AI Button - Replaces "AI Search" */}
          <button
            onClick={onOpenChat}
            className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all bg-[var(--pbfs-green)] text-white shadow-lg hover:bg-[var(--pbfs-green)]/80 flex items-center gap-2"
          >
            <span>💬</span>
            <span className="hidden sm:inline">Ask Pye AI</span>
            <span className="sm:hidden">Ask AI</span>
          </button>
        </div>
        <div className="text-white/60 text-xs hidden sm:block">
          <span>⚡ <strong>Fast:</strong> Keyword matching • Instant results</span>
        </div>
      </div>

      <div className="relative flex items-center group">
        {/* Animated Search Icon */}
        <div className={`absolute left-4 transition-all ${isSearching ? 'animate-pulse text-[var(--pbfs-green)]' : 'text-white/40'}`}>
          {isSearching ? (searchMode === 'ai' ? '✨' : '⚡') : (searchMode === 'ai' ? '✨' : '⚡')}
        </div>

        {/* Search Input */}
        <input
          type="text"
          value={localValue}
          onChange={handleChange}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              // Trigger search immediately on Enter
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
              }
              onChange(localValue);
              setShowHints(false); // Close hints when searching
            }
          }}
          onFocus={() => setShowHints(!localValue)} // Only show hints if search is empty
          onBlur={() => setTimeout(() => setShowHints(false), 300)}
          placeholder={
            searchMode === 'keyword'
              ? "⚡ Fast Search: Company, location, zip code, submitter... (Press Enter)"
              : "✨ AI Search: Type anything - questions, descriptions, natural language... (Press Enter)"
          }
          className={`w-full pl-12 pr-40 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 transition-all text-base hover:bg-white/12 font-medium ${searchMode === 'ai'
              ? 'focus:ring-[var(--pbfs-green)]/50 focus:border-[var(--pbfs-green)]'
              : 'focus:ring-[var(--pbfs-blue)]/50 focus:border-[var(--pbfs-blue)]'
            } focus:bg-white/15`}
          style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }}
        />

        {/* Right Side Controls */}
        {localValue && (
          <div className="absolute right-3 flex items-center gap-2">
            {/* Search Type Indicator - Compact */}
            {searchType && (
              <div className={`flex items-center gap-1 px-2 py-1 bg-black/20 rounded-md ${searchType.color} text-xs`}>
                <span className="text-sm">{searchType.icon}</span>
              </div>
            )}

            {/* Search Button */}
            <button
              onClick={() => {
                if (debounceTimerRef.current) {
                  clearTimeout(debounceTimerRef.current);
                }
                onChange(localValue, searchMode);
                setShowHints(false);
              }}
              className={`px-4 py-2 text-white rounded-lg transition-all text-sm font-semibold shadow-lg ${searchMode === 'ai'
                  ? 'bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80'
                  : 'bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-blue)]/80'
                }`}
              title="Search now"
            >
              Search
            </button>

            {/* Clear Button */}
            <button
              onClick={handleClear}
              className="text-white/60 hover:text-white transition-colors text-xl w-6 h-6 flex items-center justify-center"
              title="Clear search"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Search Examples Hint (Only on focus when empty) */}
      {showHints && !localValue && (
        <div className="absolute top-full left-0 right-0 mt-2 z-50">
          <div className="bg-[var(--pbfs-navy)]/95 backdrop-blur-md border border-white/20 rounded-xl p-4 shadow-2xl">
            <div className="space-y-2">
              <div className="text-white/50 text-sm font-semibold mb-2 flex items-center gap-2">
                {searchMode === 'keyword' ? (
                  <>
                    <span>⚡</span>
                    <span>Fast Search Examples:</span>
                  </>
                ) : (
                  <>
                    <span>✨</span>
                    <span>AI-Powered Search Examples:</span>
                  </>
                )}
              </div>
              <div className="space-y-1 text-xs text-white/40">
                {searchMode === 'keyword' ? (
                  <>
                    <div>• <span className="text-white/50">Houston</span> - City name</div>
                    <div>• <span className="text-white/50">77079</span> - Zip code</div>
                    <div>• <span className="text-white/50">Pye-Barker</span> - Company name</div>
                    <div>• <span className="text-white/50">Matt Hales</span> - Submitter name</div>
                    <div className="pt-2 border-t border-white/10 mt-2 text-white/30 italic">
                      ⚡ Instant results • Exact matching
                    </div>
                  </>
                ) : (
                  <>
                    <div>• <span className="text-white/50">"Houston locations needing inspection"</span> - Natural language</div>
                    <div>• <span className="text-white/50">"fire extinguishers in Texas"</span> - Semantic understanding</div>
                    <div>• <span className="text-white/50">"tags submitted this week"</span> - Time-based queries</div>
                    <div>• <span className="text-white/50">"Pye-Barker customers"</span> - Context awareness</div>
                    <div className="pt-2 border-t border-white/10 mt-2 text-white/30 italic">
                      ✨ Powered by Google Gemini AI • Understands meaning, not just keywords
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pulsing animation for search indicator */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ApprovalSearchBar;

